import React, { useEffect, useState, useCallback } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { refereeApi } from '../../services/footballApi';

interface Referee {
  id: number;
  name: string;
  long_name?: string;
  country?: { name: string } | null;
  created_at?: string;
  updated_at?: string;
}

const FootballReferees: React.FC = () => {
  const [referees, setReferees] = useState<Referee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchReferees = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await refereeApi.getAll();
      setReferees(res.data);
    } catch (err: any) {
      setError('Failed to fetch referees');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReferees();
  }, [fetchReferees]);

  const handleEdit = (id: number) => {
    navigate(`/football/referees/${id}/edit`);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this referee?')) return;
    try {
      await refereeApi.delete(id);
      fetchReferees();
    } catch (err) {
      alert('Failed to delete referee');
    }
  };

  const handleCreate = () => {
    navigate('/football/referees/new');
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 180 },
    { field: 'long_name', headerName: 'Long Name', flex: 1, minWidth: 180 },
    { field: 'country', headerName: 'Country', width: 140, valueGetter: (value: Referee['country']) => value?.name || '' },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: { row: Referee }) => (
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={() => handleEdit(params.row.id)}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}><DeleteIcon fontSize="small" /></IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Football Referees</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          Add Referee
        </Button>
      </Stack>
      <DataGrid
        rows={referees}
        columns={columns}
        loading={loading}
        autoHeight
        disableRowSelectionOnClick
        slots={{ toolbar: GridToolbar }}
        getRowId={(row) => row.id}
        sx={{ backgroundColor: 'background.paper' }}
      />
      {error && <Typography color="error">{error}</Typography>}
    </Box>
  );
};

export default FootballReferees; 